import { ReactNode, useCallback, useMemo, useRef } from 'react';
import { useCollapse } from 'react-collapsed';
import { useMount } from 'react-use';
import { isFunction } from 'lodash-es';
import cn from 'classnames';
import { useShallow } from 'zustand/react/shallow';
import { MdKeyboardArrowDown, MdOutlineVisibility, MdOutlineVisibilityOff, MdOutlinePersonAdd } from 'react-icons/md';
import { Button, useDisclosure } from '@ds';
import { InsertSpecialistToHubModal } from '@lp/features/modals/InsertSpecialistToHubModal/InsertSpecialistToHubModal';
import { HubsDataDictionary, ServiceRulesViolations } from '@lp/features/Scheduler/types';
import { getDailyAndWeeklyServiceViolations } from '@lp/features/Scheduler/validation.utils';
import { ScheduleViolationTooltip } from '@lp/features/Scheduler/components';
import { csSchedulerSelectors, useCSSchedulerStore } from '@lp/core/store/scheduler';
import { Hub, CSResource, CSResourcesWithShifts } from '@/api/apis/resources/schedule';

type HubWrapperProps = {
  hub: Hub;
  readonly?: boolean;
  initiallyExpanded?: boolean;
  initiallyNavigated?: boolean;
  userList: CSResource[];
  shiftsData: CSResourcesWithShifts;
  hubsDictionary: HubsDataDictionary;
  violations?: ServiceRulesViolations;
  children?: ReactNode | ((showHubTotals?: boolean) => ReactNode);
};

export const HubWrapper = ({
  hub,
  readonly,
  initiallyExpanded = false,
  initiallyNavigated = false,
  userList,
  shiftsData,
  hubsDictionary,
  violations,
  children,
}: HubWrapperProps) => {
  const elRef = useRef<HTMLDivElement>(null);
  const { isOpen: isInsertCSModalOpen, onOpen, onClose } = useDisclosure();
  const { isExpanded, totalsOpened } = useCSSchedulerStore(useShallow(csSchedulerSelectors.getHubState(hub.id)));
  const setHubExpand = useCSSchedulerStore.use.setHubExpand();
  const setHubTotalsOpen = useCSSchedulerStore.use.setHubTotalsOpen();
  const insertHubSpecialists = useCSSchedulerStore.use.insertHubSpecialists();
  const isEmpty = userList.length === 0;
  const showContent = !!isExpanded && !isEmpty;
  const defaultExpanded = initiallyExpanded && isExpanded === undefined;
  const { getCollapseProps, getToggleProps } = useCollapse({ isExpanded: showContent, defaultExpanded });

  const violationList = useMemo(() => getDailyAndWeeklyServiceViolations(violations), [violations]);

  const onUserListChange = (userIds: string[]) => {
    insertHubSpecialists(hub.id, userIds);
    onClose();
  };

  const renderChildren = useCallback(() => {
    return isFunction(children) ? children(totalsOpened) : children;
  }, [children, totalsOpened]);

  useMount(() => {
    if (initiallyExpanded && isExpanded === undefined) setHubExpand(hub.id, true);

    if (initiallyNavigated) {
      requestAnimationFrame(() => {
        elRef.current?.scrollIntoView({ behavior: 'smooth' });
      });
    }
  });

  return (
    <div ref={elRef} className="flex flex-col bg-white rounded-large shadow-small">
      <div className="flex justify-between items-center">
        <button
          {...getToggleProps({ onClick: () => setHubExpand(hub.id) })}
          className={cn('flex items-center gap-2 p-3', { 'opacity-60': isEmpty })}
          disabled={isEmpty}
        >
          <MdKeyboardArrowDown size={24} className={cn('text-[#111] transition', { '-rotate-90': !showContent })} />
          <span className="text-xl-sb text-[#111]">{hub.name}</span>
          {!isExpanded && violationList.length > 0 && (
            <ScheduleViolationTooltip items={violationList} withLabel>
              <div className="px-1.5 h-4 rounded-lg bg-danger-30 text-white text-s-sb">{violationList.length}</div>
            </ScheduleViolationTooltip>
          )}
        </button>
        {!readonly && (
          <Button disableRipple size="sm" variant="light" startContent={<MdOutlinePersonAdd />} onClick={onOpen}>
            Assign Specialist to Hub
          </Button>
        )}
        {isInsertCSModalOpen && (
          <InsertSpecialistToHubModal
            hub={hub}
            userList={userList}
            shiftsData={shiftsData}
            hubsDictionary={hubsDictionary}
            onClose={onClose}
            onSave={onUserListChange}
          />
        )}
      </div>
      <div {...getCollapseProps()}>
        <div className="pt-3 pl-4">
          {/* TODO: Remove elements from DOM after transition end in Collapse */}
          {/* {renderChildren()} */}
          {showContent ? renderChildren() : null}
          {!readonly && (
            <Button
              disableRipple
              size="sm"
              variant="light"
              startContent={totalsOpened ? <MdOutlineVisibilityOff /> : <MdOutlineVisibility />}
              onClick={() => setHubTotalsOpen(hub.id)}
              className="px-2 mb-3"
            >
              {totalsOpened ? 'Hide' : 'Show'} Hub Totals
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
